import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Paper,
  Pagination,
  InputAdornment,
  Checkbox,
  alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { MediaFile, MediaFilterRequest } from "../../types/media";
import { getAllMedia } from "../../services/mediaService";
import { getStaticFileUrl } from "../../config/apiConfig";

interface MediaLibraryModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (images: MediaFile[]) => void;
  multiSelect?: boolean;
  maxSelect?: number;
  mediaType?: string;
}

const MediaLibraryModal: React.FC<MediaLibraryModalProps> = ({
  open,
  onClose,
  onSelect,
  multiSelect = false,
  maxSelect = 10,
  mediaType = "Product",
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const filter: MediaFilterRequest = {
        page,
        pageSize,
        search: search || undefined,
        mediaType,
        sortBy: "CreatedAt",
        sortDescending: true,
      };
      const response = await getAllMedia(filter);
      if (response.isSuccess && response.data) {
        setMediaFiles(response.data.items);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Failed to fetch media:", error);
    } finally {
      setLoading(false);
    }
  }, [page, search, mediaType]);

  useEffect(() => {
    if (open) {
      fetchMedia();
    }
  }, [open, fetchMedia]);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setSearch("");
      setPage(1);
    }
  }, [open]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    fetchMedia();
  };

  const handleImageClick = (media: MediaFile) => {
    if (multiSelect) {
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(media.id)) {
          newSet.delete(media.id);
        } else if (newSet.size < maxSelect) {
          newSet.add(media.id);
        }
        return newSet;
      });
    } else {
      // Single select - immediately select and close
      onSelect([media]);
      onClose();
    }
  };

  const handleConfirmSelection = () => {
    const selectedImages = mediaFiles.filter((m) => selectedIds.has(m.id));
    onSelect(selectedImages);
    onClose();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const resolveImageUrl = (relativePath: string) => {
    try {
      new URL(relativePath);
      return relativePath;
    } catch {
      return getStaticFileUrl(relativePath);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: "70vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ImageIcon color="primary" />
          <Typography variant="h6">{t("admin.mediaLibrary.title")}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search Bar */}
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{ mb: 2 }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={t("admin.mediaLibrary.searchPlaceholder")}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {!loading && mediaFiles.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
              color: "text.secondary",
            }}
          >
            <ImageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1">{t("admin.mediaLibrary.noImages")}</Typography>
            <Typography variant="body2">
              {search
                ? t("admin.mediaLibrary.tryDifferentSearch")
                : t("admin.mediaLibrary.uploadFirst")}
            </Typography>
          </Box>
        )}

        {/* Image Grid */}
        {!loading && mediaFiles.length > 0 && (
          <Grid container spacing={2}>
            {mediaFiles.map((media) => {
              const isSelected = selectedIds.has(media.id);
              return (
                <Grid item xs={6} sm={4} md={3} key={media.id}>
                  <Paper
                    onClick={() => handleImageClick(media)}
                    sx={{
                      position: "relative",
                      aspectRatio: "1/1",
                      overflow: "hidden",
                      borderRadius: 1,
                      cursor: "pointer",
                      border: isSelected ? "3px solid" : "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: 2,
                        transform: "scale(1.02)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={resolveImageUrl(media.relativePath)}
                      alt={media.originalName || media.fileName}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/placeholder-product.svg";
                      }}
                    />

                    {/* Selection Indicator */}
                    {multiSelect && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          left: 4,
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleImageClick(media)}
                          sx={{
                            p: 0,
                            bgcolor: "rgba(255,255,255,0.9)",
                            borderRadius: 0.5,
                            "&:hover": { bgcolor: "white" },
                          }}
                        />
                      </Box>
                    )}

                    {/* Selected Overlay */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: alpha("#1976d2", 0.15),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        {!multiSelect && (
                          <CheckCircleIcon
                            sx={{
                              fontSize: 48,
                              color: "primary.main",
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {/* File Info */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "white",
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "10px",
                        }}
                      >
                        {media.originalName || media.fileName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "9px", opacity: 0.8 }}
                      >
                        {media.fileSizeFormatted}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {multiSelect
            ? t("admin.mediaLibrary.selectedCount", { count: selectedIds.size, max: maxSelect })
            : t("admin.mediaLibrary.clickToSelect")}
        </Typography>
        <Button onClick={onClose} color="inherit">
          {t("common.cancel")}
        </Button>
        {multiSelect && (
          <Button
            variant="contained"
            onClick={handleConfirmSelection}
            disabled={selectedIds.size === 0}
          >
            {t("admin.mediaLibrary.select", { count: selectedIds.size })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MediaLibraryModal;
